import type { ButtonHTMLAttributes } from "react"
import { useTranslation } from "react-i18next"

import { Spinner } from "@/components/ui/spinner"

import TiktokIcon from "./tiktok-icon"

export interface TiktokConnectCtaProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "type"
> {
  /** Render the inline spinner + processing copy. Mirrors the parent
   *  Inertia `<Form>` render-prop's `processing` flag. */
  processing?: boolean
  /**
   * Override the button copy. Defaults to the i18n key
   * `signup.tiktok.cta` ("Continue with TikTok" / "Continuer avec TikTok"
   * — CTPO-locked per UX Spec §2). Settings revoked-state Reconnect
   *  passes a different key (`settings.social_accounts.tiktok.reconnect_cta`).
   */
  labelKey?: string
  /** Same axis as `labelKey` but for the in-flight processing copy. */
  processingLabelKey?: string
  /**
   * Form-submit semantics by default — the parent wraps this in an
   * Inertia `<Form>` and the button posts via the form's action. Pass
   * `type="button"` (rare) only when the CTA isn't a form submit.
   */
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"]
  /** Stable test selector. Defaults to `tiktok-connect` (matches
   *  OKS-22's frozen testid contract). */
  "data-testid"?: string
}

/**
 * TikTok-style Connect CTA — CTPO-locked tokens.
 *
 * Used in TWO places (UX Spec §2 + §1.D):
 *   1. **OKS-22 Step 3** ("Continue with TikTok" — TikTok partner-styled
 *      button initiating the OAuth handoff).
 *   2. **Settings revoked-state Reconnect** (same tokens, different copy).
 *
 * Tokens (DO NOT FORK):
 *
 * | Token | Value |
 * | -- | -- |
 * | Background | `#000000` (TikTok black) |
 * | Text color | `#FFFFFF` |
 * | Font | system-ui / SF Pro 16px medium (500), no letter-spacing |
 * | Height | 48px desktop, 56px mobile (≥44px touch target) |
 * | Padding | 12/24px desktop, 16/24px mobile |
 * | Border radius | 4px |
 * | Glyph | `<TiktokIcon>` 24×24 (20×20 in inline button context per UX Spec) |
 * | Glyph spacing | 12px right-margin between glyph + text |
 * | Hover | 4% white-overlay (Tailwind `hover:opacity-90` approximates) |
 * | **Focus** | **2px white outline + 2px offset (MANDATORY)** |
 * | Disabled | 50% opacity |
 *
 * The focus ring is non-negotiable — without it, keyboard users on the
 * dark button see nothing on focus (CTPO-emphasized). Vitest snapshot
 * canary asserts the `outline:2px_solid_#fff` + `outline-offset:2px`
 * focus-visible class is present.
 *
 * a11y: `aria-describedby` slot lets the parent link an `<span
 * className="sr-only">` with context (e.g. handle echo on Step 3, or
 * "Reconnect TikTok account" on Settings revoked).
 */
export function TiktokConnectCta({
  processing = false,
  labelKey = "signup.tiktok.cta",
  processingLabelKey = "signup.tiktok.cta_processing",
  type = "submit",
  className = "",
  disabled,
  "data-testid": testId = "tiktok-connect",
  ...rest
}: TiktokConnectCtaProps) {
  const { t } = useTranslation()
  // Either flag disables the button. Boolean cast keeps the logic clear
  // for the ESLint `prefer-nullish-coalescing` rule (both operands are
  // boolean by intent — neither is a "missing" sentinel).
  const isDisabled = Boolean(disabled) || processing
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      data-testid={testId}
      style={{ backgroundColor: "#000000", color: "#ffffff" }}
      className={[
        // Layout
        "inline-flex h-14 w-full min-w-0 items-center justify-center gap-3 rounded-[4px] px-6 text-base font-medium",
        // States
        "transition-opacity hover:opacity-90",
        // CTPO-mandatory keyboard focus ring (white outline + offset on
        // black bg). DO NOT remove or change colour — this is the only
        // visibility cue keyboard users get on this button.
        "focus-visible:[outline:2px_solid_#fff] focus-visible:outline focus-visible:[outline-offset:2px]",
        // Disabled
        "disabled:pointer-events-none disabled:opacity-50",
        // Responsive
        "sm:h-12 sm:w-auto sm:flex-1",
        className,
      ].join(" ")}
      {...rest}
    >
      {processing && <Spinner className="size-4 text-white" />}
      <span aria-hidden="true" className="inline-flex items-center">
        <TiktokIcon width={20} height={20} />
      </span>
      {processing ? t(processingLabelKey) : t(labelKey)}
    </button>
  )
}

export default TiktokConnectCta
